import { trackGaEvent } from "./ga4";
import {
  consumeGoogleSignupPending,
  markGoogleSignupPending,
  trackMetaEvent,
} from "./metaPixel";

const REDIRECT_FLUSH_MS = 500;

/**
 * Resolve after trackers have had a chance to send, or after timeout.
 * Needed before OAuth redirects that unload the page.
 * @param {(done: () => void) => void} fireWithCallback
 * @returns {Promise<void>}
 */
function waitForTrackingFlush(fireWithCallback) {
  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    const timer = window.setTimeout(done, REDIRECT_FLUSH_MS);
    fireWithCallback(() => {
      window.clearTimeout(timer);
      done();
    });
  });
}

/**
 * Signup funnel start (button click).
 * @param {"email" | "google"} method
 */
export function trackSignUpStart(method) {
  trackMetaEvent("SignUpStart", { method });
  trackGaEvent("sign_up_start", { method });
}

/**
 * Fire signup-start events and wait briefly so a redirect does not drop them.
 * @param {"email" | "google"} method
 * @returns {Promise<void>}
 */
export function trackSignUpStartBeforeRedirect(method) {
  trackMetaEvent("SignUpStart", { method });

  return waitForTrackingFlush((done) => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "sign_up_start", {
        method,
        event_callback: done,
        event_timeout: REDIRECT_FLUSH_MS,
      });
      return;
    }
    // Meta has no reliable callback — timeout in waitForTrackingFlush covers it.
  });
}

/**
 * Signup completed (API / OAuth success — not a click).
 * @param {"email" | "google"} [method]
 */
export function trackSignUpComplete(method) {
  trackMetaEvent("CompleteRegistration");
  trackGaEvent("sign_up", method ? { method } : undefined);
}

/**
 * Google button: mark pending signup, fire start events, then run redirect.
 * @param {() => void} redirect
 */
export async function beginGoogleSignup(redirect) {
  markGoogleSignupPending();
  await trackSignUpStartBeforeRedirect("google");
  redirect();
}

export { markGoogleSignupPending, consumeGoogleSignupPending };
